"use client";

import {useEffect, useRef, useState, type ReactNode} from "react";
import clsx from "clsx";
import Icon from "@/components/library/Icon";

/** Show a scroll cue only while more review content remains below the viewport. */
export default function ApprovalReviewScrollArea({children, enabled = true, label, className}: {
   children: ReactNode;
   enabled?: boolean;
   label: string;
   className?: string;
}) {
   const viewportRef = useRef<HTMLElement>(null);
   const contentRef = useRef<HTMLDivElement>(null);
   const [canScrollDown, setCanScrollDown] = useState(false);

   useEffect(() => {
      const viewport = viewportRef.current;
      const content = contentRef.current;
      if (!enabled || !viewport || !content) return;
      const update = () => setCanScrollDown(viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop > 2);
      update();
      viewport.addEventListener("scroll", update, {passive: true});
      const observer = new ResizeObserver(update);
      observer.observe(viewport);
      observer.observe(content);
      return () => {
         viewport.removeEventListener("scroll", update);
         observer.disconnect();
      };
   }, [enabled]);

   if (!enabled) return <div className={className}>{children}</div>;

   return <div className="relative flex min-h-0 flex-1 flex-col">
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <section ref={viewportRef} tabIndex={0} aria-label={label}
         className={clsx("@container min-h-0 flex-1 overflow-y-auto overscroll-contain", className)}>
         <div ref={contentRef} className="flow-root">{children}</div>
      </section>
      {canScrollDown && <div aria-hidden="true" data-testid="approval-scroll-cue"
         className="pointer-events-none absolute inset-x-0 bottom-0 flex h-14 items-end justify-center bg-gradient-to-t from-panel via-panel/80 to-transparent pb-2">
         <span className="flex h-7 w-7 items-center justify-center rounded-full border border-neutral bg-panel text-500 shadow-sm">
            <Icon icon="arrow-down" className="h-3.5 w-3.5" />
         </span>
      </div>}
   </div>;
}
